<script lang="ts" setup>
import { QBtn } from 'quasar'
import { computed } from 'vue'
import { Color } from '@previo/codebooks'
import PIcon from '../icon/PIcon.vue'
import PLoader from '../loader/PLoader.vue'
import brandColors from '../../assets/scss/shared/_brand.module.scss'
import { PBtnTableSize } from './types'

type Props = {
  icon: string
  color?:
    | Color.PRIMARY
    | Color.POSITIVE
    | Color.NEGATIVE
    | Color.WARNING
    | Color.TRANSPARENT
    | Color.PRIMARY_TRANSPARENT
    | Color.GREY_600
  disable?: boolean
  loading?: boolean
  size?: null | PBtnTableSize
  cy: string
  href?: string
  hrefTarget?: string
}

const props = withDefaults(defineProps<Props>(), {
  color: Color.PRIMARY,
  disable: false,
  loading: false,
  size: PBtnTableSize.MEDIUM,
})

type Emits = {
  (e: 'click', evt: Event): void
}

const emits = defineEmits<Emits>()

const colorBg = computed<
  | Color.PRIMARY_TRANSPARENT
  | Color.POSITIVE_TRANSPARENT
  | Color.NEGATIVE_TRANSPARENT
  | Color.WARNING_TRANSPARENT
  | Color.TRANSPARENT
  | Color.GREY_200
  | Color.GREY_600
>(() => {
  if (props.disable && props.color === Color.PRIMARY_TRANSPARENT) {
    return Color.TRANSPARENT
  }

  if (props.disable) {
    return Color.GREY_200
  }

  switch (props.color) {
    case Color.NEGATIVE:
      return Color.NEGATIVE_TRANSPARENT
    case Color.WARNING:
      return Color.WARNING_TRANSPARENT
    case Color.POSITIVE:
      return Color.POSITIVE_TRANSPARENT
    case Color.PRIMARY:
      return Color.PRIMARY_TRANSPARENT
    case Color.TRANSPARENT:
      return Color.TRANSPARENT
    case Color.PRIMARY_TRANSPARENT:
      return Color.TRANSPARENT
    case Color.GREY_600:
      return Color.GREY_200
    default:
      throw 'Invalid color props in PBtnTable component'
  }
})

const colorIconAndLoading = computed<
  | Color.PRIMARY
  | Color.PRIMARY_DARK
  | Color.NEGATIVE_DARK
  | Color.WARNING_DARK
  | Color.POSITIVE_DARK
  | Color.GREY_300
  | Color.GREY_500
  | Color.GREY_600
>(() => {
  if (props.disable) {
    switch (props.color) {
      case Color.PRIMARY_TRANSPARENT:
      case Color.TRANSPARENT:
        return Color.GREY_300

      default:
        return Color.GREY_500
    }
  }

  switch (props.color) {
    case Color.PRIMARY:
      return Color.PRIMARY_DARK
    case Color.POSITIVE:
      return Color.POSITIVE_DARK
    case Color.NEGATIVE:
      return Color.NEGATIVE_DARK
    case Color.WARNING:
      return Color.WARNING_DARK
    case Color.TRANSPARENT:
      return Color.GREY_600
    case Color.GREY_600:
      return Color.GREY_600
    case Color.PRIMARY_TRANSPARENT:
      return Color.PRIMARY

    default:
      throw 'Invalid color props in PBtnTable component'
  }
})

const hexHoverBorderColor = computed<string>(() => {
  switch (props.color) {
    case Color.TRANSPARENT:
    case Color.PRIMARY_TRANSPARENT:
      return Color.TRANSPARENT
    default:
      return brandColors[colorIconAndLoading.value as keyof typeof brandColors]
  }
})

const hexBgDisabledColor = computed<string>(() => {
  switch (props.color) {
    case Color.PRIMARY_TRANSPARENT:
      return Color.TRANSPARENT
    default:
      return brandColors[Color.GREY_200 as keyof typeof brandColors]
  }
})

const hexHoverBgColor = computed<string>(() => {
  switch (props.color) {
    case Color.TRANSPARENT:
      return brandColors[Color.GREY_200 as keyof typeof brandColors] || Color.TRANSPARENT
    case Color.PRIMARY_TRANSPARENT:
      return brandColors[Color.PRIMARY_TRANSPARENT as keyof typeof brandColors] || Color.TRANSPARENT
    case Color.GREY_600:
      return brandColors[Color.GREY_200 as keyof typeof brandColors] || Color.TRANSPARENT
    default:
      return (
        brandColors[(props.color + 'Transparent') as keyof typeof brandColors] || Color.TRANSPARENT
      )
  }
})

const click = (e: Event): void => {
  emits('click', e)
}
</script>

<template>
  <q-btn
    class="ds-btn-table p-transition-duration"
    :class="{ 'ds-btn-table__small': size == PBtnTableSize.SMALL }"
    unelevated
    no-caps
    :color="colorBg"
    :disable="disable"
    :loading="loading"
    :data-cy="cy"
    :href="href"
    :target="hrefTarget"
    :style="{
      '--border-color': hexHoverBorderColor,
      '--hover-color': hexHoverBgColor,
      '--disabled-bg-color': hexBgDisabledColor,
    }"
    @click="click"
  >
    <template #loading>
      <p-loader :color="colorIconAndLoading" />
    </template>
    <p-icon :icon="icon" :color="colorIconAndLoading" />
    <slot />
  </q-btn>
</template>

<style lang="scss" scoped>
@import '../../assets/scss/shared/mixins';
@import '../../assets/scss/shared/ds.variables.module';

.ds-btn-table {
  border-radius: $global-border-radius;

  @include mobile() {
    padding: 8px;
  }

  @include laptopAndLarge() {
    padding: 6px;
  }

  &__small {
    font-size: 8px; // Override font size in

    @include laptopAndLarge() {
      padding: 2px; // Fit into PCountInputField
    }
  }
}

.ds-btn-table {
  &:hover {
    &:not(.q-btn.disabled) {
      box-shadow: 0 0 0 1px var(--border-color) inset;
      background: var(--hover-color) !important;
    }
  }

  &.disabled {
    background: var(--disabled-bg-color);
  }
}
</style>
